import React, { JSX, useContext, useEffect, useState } from "react";
import { cloneDeep } from "lodash";
import { getNetworkTooltip } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/utility";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ColumnConfig, printNumberAsString, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { TextAlignment, WafflesThemes } from "@app/shared/waffles/exports";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { FontAwesome } from "@app/shared/icons/qesIcons";
import QuestProprietaryDataIcon from "@app/overviews/network-benchmarking-dashboard/filters/QuestProprietaryDataIcon";
import { BenchmarkingFiltersContext } from "@app/overviews/network-benchmarking-dashboard/filters/BenchmarkingFiltersContext";
import { CompetitiveInsightsTabTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/CompetitiveInsightsTabTypeEnum";
import { CompetitiveInsightsCountyFilterTypeEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/CompetitiveInsightsCountyFilterType";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESButton } from "@app/shared/qes-standards/Button";
import QHPNetworkFlyout from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/QHPNetworkFlyout";
import { QHPNetwork } from "@app/overviews/network-benchmarking-dashboard/marketplace/filters/shared/QHPNetwork";

interface QHPNetworkFiltersTableProps {
  colorMap?: Map<string, string>;
  data: QHPNetwork[];
  isMapView?: boolean;
  isNetworkSummary?: boolean;
  parentOrganizationName: string;
  selectedContractIds: string[];
}

interface QHPNetworkFiltersTableData {
  contractName: JSX.Element;
  countyCount: JSX.Element;
  opportunityCountyCount: JSX.Element;
  rating: JSX.Element;
  rowIsDisabled?: boolean;
  type: JSX.Element;
  [wafflesKeyColumnName]: string;
}

const QHPNetworkFiltersTable = (props: QHPNetworkFiltersTableProps): JSX.Element => {
  const [showingMore, setShowingMore] = useState<boolean>(false);
  const [tableData, setTableData] = useState<QHPNetworkFiltersTableData[]>([]);

  const benchmarkingFiltersContext = useContext(BenchmarkingFiltersContext);

  useEffect(() => {
    // on load, if there are selected contracts (from a previous search, or from applying service area),
    // check to see if they are in the "load more" part of a table (if so, open it!)
    if (cloneDeep(props.data).slice(3).map(x => x.contractId).some(y => props.selectedContractIds.includes(y))) {
      if (!showingMore) {
        setShowingMore(true);
      }
    } else {
      if (showingMore) {
        setShowingMore(false);
      }
    }
  }, [props.data]);

  const handleFlyoutOpen = (): void => {
    if (benchmarkingFiltersContext.selectedTab === CompetitiveInsightsTabTypeEnum.NetworkSelection) {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
    }
  }

  useEffect(() => {
    if (!!props.data) {
      /* oxlint-disable sort-keys */
      const end = props.isNetworkSummary ? 5 : 3;
      const newTableData: QHPNetworkFiltersTableData[] = (showingMore ? props.data : props.data.slice(0, end)).map((contract, index) => {
        return {
          [wafflesKeyColumnName]: contract.contractId || index.toString(),
          contractName: (
            <>
              <FlyoutLink
                classNames="qes-button qes-button-secondary h-contract-flyout-link-btn"
                testId={`network-benchmarking-dashboard--network-filters__flyout-link-${contract.contractId}`}
                onOpen={handleFlyoutOpen}
                content={{
                  body: (
                    <QHPNetworkFlyout
                      contract={contract}
                    />
                  ),
                  title: `Network ${getNetworkId(contract)}`,
                }}
              >
                {props.isNetworkSummary && (
                  <QesIcon
                    style={{ color: props.colorMap?.get(contract.contractId), fill: props.colorMap?.get(contract.contractId) }}
                    iconName={FontAwesome.Square}
                    width="25"
                    className="me-3"
                    description="Open network flyout"
                  />
                )}
                <span className="h-contract-flyout-link">
                  <TooltipWrapper
                    text={getNetworkTooltip(contract)}
                  >
                    {contract.networkName}
                  </TooltipWrapper>
                </span>
              </FlyoutLink>
              {contract.isQesNetwork && <QuestProprietaryDataIcon />}
              {contract.dentalOnly && (
                <TooltipWrapper
                  containerClassName="me-1"
                  text="Stand-Alone Dental Plan (SADP) Network"
                >
                  <img className="d-block" src={`${window.customUIBaseUrl}/images/purple-tooth.svg`} alt="" />
                </TooltipWrapper>
              )}
            </>
          ),
          type: (
            <React.Fragment>
              {!!contract.planTypes?.length ? (
                contract.planTypes.map((planType, index) => (
                  <div key={index}>
                    {getPlanTypeShortenedName(planType.planType)}
                  </div>
                )
              )) : (
                "---"
              )}
            </React.Fragment>
          ),
          countyCount: (
            <React.Fragment>
              {!!contract.planTypes?.length ? (
                contract.planTypes.map((planType, index) => (
                  <div key={index}>
                    {printNumberAsString(contract.countyCount, "---")}
                  </div>
                )
              )) : (
                printNumberAsString(contract.countyCount, "---")
              )}
            </React.Fragment>
          ),
          opportunityCountyCount: (
            <React.Fragment>
              {!!contract.planTypes?.length ? (
                contract.planTypes.map((planType, index) => (
                  <div key={index}>
                    {printNumberAsString(contract.opportunityCountyCount, "---")}
                  </div>
                )
                )) : (
                printNumberAsString(contract.opportunityCountyCount, "---")
              )}
            </React.Fragment>
          ),
          rating: (
            <React.Fragment>
              {!!contract.planTypes?.length ? (
                contract.planTypes.map((planType, index) => (
                  <div key={index}>
                    {printNumberAsString(planType.starRating, "---")}
                  </div>
                )
              )) : (
                "---"
              )}
            </React.Fragment>
          ),
          // disable row if contract is disabled (due to lack of data) OR
          // five contracts have already been selected and the row isn't one of the selected h contracts
          rowIsDisabled: contract.disabled || (
            benchmarkingFiltersContext.selectedNetworks.length === 5 && !benchmarkingFiltersContext.selectedNetworks.map(x => x.contractId).includes(contract.contractId)
          ),
        }
      });
      /* oxlint-enable sort-keys */

      setTableData(newTableData);
    }
  }, [props.data, benchmarkingFiltersContext.selectedNetworks, showingMore]);

  const columnConfig: ColumnConfig = {
    contractName: {
      alignment: TextAlignment.Left,
      displayText: "",
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "contract-name",
    },
    countyCount: {
      alignment: TextAlignment.Center,
      displayElement: <>{props.parentOrganizationName === "Your QES Networks" ? "Current" : "Active"}<br />County Count</>,
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "county-count",
    },
    opportunityCountyCount: {
      alignment: TextAlignment.Center,
      displayElement: <>Opportunity<br />County Count</>,
      hidden: props.parentOrganizationName !== "Your QES Networks",
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "opportunity-county-count",
    },
    rating: {
      alignment: TextAlignment.Center,
      displayElement: <><br />Rating</>,
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "rating",
    },
    type: {
      alignment: TextAlignment.Center,
      displayElement: <><br />Type</>,
      noResize: true,
      noSort: true,
      sizeContentToFit: true,
      testId: "plan-type",
    },
  };

  const getNetworkId = (contract: QHPNetwork): string => {
    const splitContract: string[] = contract.contractId.split("_");

    if (!!splitContract?.length) {
      return splitContract[1];
    }
  }

  const getPlanTypeShortenedName = (planType: string): string => {
    if (!planType) {
      return "";
    } else if (planType.toUpperCase().includes("MEDICARE-MEDICAID")) {
      return "MMP";
    } else if (planType.toUpperCase().includes("1876 COST")) {
      return "1876";
    } else if (planType.toUpperCase().includes("HMO/HMOPOS")) {
      return "HMO";
    } else if (planType.toUpperCase().includes("LOCAL PPO") || planType.toUpperCase().includes("REGIONAL PPO")) {
      return "PPO";
    } else {
      return planType;
    }
  }

  const handleCheckboxChange = (selectedKeys: string[]): void => {
    // obtain the h contract models based off their contract ids
    const selectedHContracts: QHPNetwork[] = cloneDeep(props.data).filter(
      (x) => selectedKeys.includes(x.contractId.toString())
    );

    // since toggling a waffles checkbox returns all of the currently-selected keys
    // and the selected h contracts array can include h contracts from different parent organizations,
    // filter out all h contracts that correspond to this parent organization from the select h contracts...
    const newSelectedHContracts: QHPNetwork[] = cloneDeep((benchmarkingFiltersContext.selectedNetworks as QHPNetwork[])).filter(
      (x) => !props.data.map(d => d.contractId).includes(x.contractId)
    );

    // ...and then add back the selected h contracts for this parent organization
    benchmarkingFiltersContext.setSelectedNetworks(newSelectedHContracts.concat(selectedHContracts));
  }

  const onRowMouseOver = (row: QHPNetworkFiltersTableData): void => {
    if (!!row) {
      const network: QHPNetwork = props.data.find(x => x.contractId === row[wafflesKeyColumnName]);

      if (!!network) {
        benchmarkingFiltersContext.setMousedOverCountyIds(network.networkCounties.map(nc => nc.countyId.toString()));
        benchmarkingFiltersContext.setMousedOverNetwork(network);
      }
    } else {
      benchmarkingFiltersContext.setMousedOverCountyIds([]);
      benchmarkingFiltersContext.setMousedOverNetwork(null);
    }
  }

  return (
    <React.Fragment>
      {props.parentOrganizationName === "Your QES Networks" && props.isMapView && (
        <div className="d-flex flex-wrap">
          {Object.values(CompetitiveInsightsCountyFilterTypeEnum).map((filterType, index) => (
            <QESButton
              key={index}
              qesTestId={`network-benchmarking-dashboard__network-filters--county-chip-${filterType.toLowerCase()}`}
              qesType="none"
              className={`county-filter-type-pill me-2${benchmarkingFiltersContext.selectedCountyFilterType.includes(filterType) ? " county-filter-type-pill--active" : ""}`}
              onClick={() => benchmarkingFiltersContext.toggleSelectedCountyFilterType(filterType)}
            >
              {filterType}
            </QESButton>
          ))}
        </div>
      )}
      <Waffles<QHPNetworkFiltersTableData>
        columns={columnConfig}
        data={tableData}
        disableProcessingOverlay={true}
        disableStickyHeader={true}
        fullWidth={true}
        handleCheckboxChange={handleCheckboxChange}
        keyColumns={["contractName"]}
        onRowMouseOver={onRowMouseOver}
        selectedKeys={benchmarkingFiltersContext.selectedNetworks.map((x) => x.contractId.toString())} //adjust so it's only for this tile
        showCheckboxes={!props.isNetworkSummary}
        testId={`network-benchmarking-dashboard--network-filters__table-${props.parentOrganizationName?.replace(/\s+/g, '-').toLowerCase() }`}
        theme={WafflesThemes.Simple}
      />
      {(props.data.length > (props.isNetworkSummary ? 5 : 3)) && (
        <QESButton
          qesTestId={`network-benchmarking-dashboard__network-filters--table-show-more-less-${props.parentOrganizationName?.replace(/\s+/g, '-').toLowerCase() }`}
          qesType="secondary"
          onClick={() => setShowingMore(!showingMore)}
          className="px-0 float-end"
        >
          Show {showingMore ? "Less" : "More"}
        </QESButton>
      )}
    </React.Fragment>

  )
}

export default QHPNetworkFiltersTable;